<style scoped>
::v-deep .form-row > .form-label {
    @apply font-bold;
}

.form-select {
    width: 100%;
}

.require-message::before {
    content: '*';
    @apply text-red inline-block pr-1;
}

.progress-bar {
    border-width: 0.0625rem;
    height: 1.625rem;
    text-align: center;
    position: relative;
    @apply w-full border-primary rounded;
}

.tooltip {
    position: relative;
    display: inline-block;
}

.tooltip .tooltiptext {
    font-size: 0.75rem;
    visibility: hidden;
    width: 150px;
    color: black;
    line-height: 1.5;
    background-color: #fff2cc;
    border-color: #ffbf00;
    border-width: 1px;
    text-align: center;
    padding: 5px 0;

    position: absolute;
    top: -10px;
    z-index: 1;
    top: 115%;
    right: -30%;

    @apply font-normal;
}

.tooltip:hover .tooltiptext {
    visibility: visible;
}
</style>

<template>
    <div>
        <component :is="formWrapper" class="account-info-form-wrapper">
            <formFieldRow
                name="customerId"
                :label="$t('account.customer.id')"
                :errors="errors"
                class="field-customerId"
            >
                <template #default="{ hasError }">
                    <input
                        class="form-input w-full"
                        :class="{ 'border-red': hasError }"
                        :value="customerId"
                        :readonly="true"
                    />
                </template>
            </formFieldRow>
            <formFieldRow
                name="customerTypeCode"
                :label="$t('account.type')"
                :errors="errors"
                class="field-customerTypeCode"
            >
                <template #default="{hasError}">
                    <input
                        class="form-input w-full"
                        :class="{ 'border-red': hasError }"
                        :value="$t(`account.type.${customerTypeCode}`)"
                        readonly
                    />
                </template>
            </formFieldRow>
            <formFieldRowByRule
                name="deliverySite"
                :label="$t('account.delivery.site')"
                :errors="errors"
                :rules="validationRules"
                class="field-deliverySite"
            >
                <template #default="{ hasError }">
                    <select
                        class="form-select"
                        :class="{ 'border-red': hasError }"
                        v-model.trim="form.deliverySite"
                    >
                        <option disabled :value="null"> </option>
                        <option v-for="s in siteList" :key="s.id" :value="s.id">
                            {{ s.cmpySiteName }}
                        </option>
                    </select>
                </template>
            </formFieldRowByRule>
            <formFieldRowByRule
                :name="['surname', 'givenName']"
                :label="$t('account.name')"
                :errors="errors"
                :rules="validationRules"
                class="field-surname field-givenName"
            >
                <template #default="{hasError}">
                    <div class="flex flex-col sm:flex-row items-start">
                        <input
                            class="form-input w-full mb-2 sm:w-32 sm:mr-2 sm:mb-0"
                            :class="{ 'border-red': hasError }"
                            type="text"
                            v-model.trim="form.surname"
                            :placeholder="$t('account.surname')"
                        />
                        <input
                            class="form-input w-full sm:flex-1"
                            :class="{ 'border-red': hasError }"
                            type="text"
                            v-model.trim="form.givenName"
                            :placeholder="$t('account.given.name')"
                        />
                    </div>
                </template>
            </formFieldRowByRule>
            <formFieldRowByRule
                name="departmentName"
                :label="$t('account.department')"
                :errors="errors"
                :rules="validationRules"
                class="field-departmentName"
            >
                <template #default="{hasError}">
                    <div class="flex flex-col sm:flex-col-reverse">
                        <div class="text-blue text-sm">
                            {{ $t('account.department.remind.message') }}
                        </div>

                        <input
                            class="form-input w-full"
                            :class="{ 'border-red': hasError }"
                            v-model.trim="form.departmentName"
                        />
                    </div>
                </template>
            </formFieldRowByRule>
            <formFieldRowByRule
                name="title"
                :label="$t('account.title')"
                :errors="errors"
                :rules="validationRules"
                class="field-title"
            >
                <template #default="{ hasError }">
                    <select
                        class="form-select"
                        v-model.trim="form.title"
                        :class="{ 'border-red': hasError }"
                    >
                        <option disabled :value="null"> </option>
                        <option v-for="c in genderCodeList" :key="c" :value="c">
                            {{ $t('account.title.' + c) }}
                        </option>
                    </select>
                </template>
            </formFieldRowByRule>
            <formFieldRowByRule
                name="occupation"
                :label="$t('account.occupation')"
                :errors="errors"
                :rules="validationRules"
                class="field-occupation"
            >
                <template #default="{ hasError }">
                    <select
                        class="form-select"
                        v-model.trim="form.occupation"
                        :class="{ 'border-red': hasError }"
                    >
                        <option disabled :value="null"> </option>
                        <option
                            v-for="o in occupationList"
                            :key="o.id"
                            :value="o.id"
                        >
                            {{ $t(o.slctDesp) }}
                        </option>
                    </select>
                </template>
            </formFieldRowByRule>
            <formFieldRowByRule
                name="jobLevel"
                :label="$t('account.job.level')"
                :errors="errors"
                :rules="validationRules"
                class="field-jobLevel"
            >
                <template #default="{hasError}">
                    <select
                        class="form-select"
                        v-model.trim="form.jobLevel"
                        :class="{ 'border-red': hasError }"
                    >
                        <option disabled :value="null"></option>
                        <option
                            v-for="j in jobLevelList"
                            :key="j.id"
                            :value="j.id"
                        >
                            {{ $t(j.slctDesp) }}
                        </option>
                    </select>
                </template>
            </formFieldRowByRule>
            <formFieldRowByRule
                name="email"
                :label="$t('account.email')"
                :errors="errors"
                :rules="validationRules"
                class="field-email"
            >
                <template #default="{ hasError }">
                    <input
                        class="form-input w-full"
                        :class="{ 'border-red': hasError }"
                        v-model.trim="email"
                        type="email"
                        :readonly="!allowEditEmail"
                    />
                    <div class="text-blue" v-if="allowEditEmail">
                        {{ $t('account.email.ask.for.input') }}
                    </div>
                </template>
            </formFieldRowByRule>
            <formFieldRowByRule
                name="monthlyIncome"
                :label="$t('account.monthly.income')"
                :errors="errors"
                :rules="validationRules"
                class="field-monthlyIncome"
            >
                <template #default="{hasError}">
                    <select
                        class="form-select"
                        v-model.trim="form.monthlyIncome"
                        :class="{ 'border-red': hasError }"
                    >
                        <option disabled :value="null"></option>
                        <option
                            v-for="j in monthlyIncomeList"
                            :key="j.id"
                            :value="j.id"
                        >
                            {{ $t(j.slctDesp) }}
                        </option>
                    </select>
                </template>
            </formFieldRowByRule>

            <formFieldRowByRule
                name="birthday"
                :label="$t('account.birthday')"
                :errors="errors"
                :rules="validationRules"
                class="field-birthday"
            >
                <template>
                    <monthPicker
                        v-if="allowEditBirthday"
                        :value="form.birthday"
                        @input="
                            v => {
                                form.birthday = v
                                    ? v.valueFormatted
                                    : undefined;
                            }
                        "
                        :minYear="new Date().getFullYear() - 100"
                        :maxYear="new Date().getFullYear() - 18"
                        :defaultYear="new Date().getFullYear() - 50"
                        :format="'yyyy-MM-dd'"
                    />
                    <input
                        v-else
                        class="form-input w-full"
                        :value="formatDate(form.birthday, 'yearMonth')"
                        readonly
                    />
                    <div class="text-xs text-blue">
                        {{ $t('account.birthday.remind.message') }}
                    </div>
                    <div
                        v-if="
                            showBirthdayAwardMessage &&
                                canGetBirthdayAward &&
                                brthAwardMinPymtRqr !== 0
                        "
                    >
                        <div v-if="birthAwardDistributed" class="text-blue">
                            {{
                                $t(
                                    'account.birthday.award.distributed',
                                    lastBrthAwardIssYear
                                )
                            }}
                        </div>
                        <div v-else-if="achievedMinPymtRqr" class="text-blue">
                            {{ '生日獎賞進度已完成！獎賞將會於生日月份派發！' }}
                        </div>
                        <div v-else class="flex">
                            <div class="flex-shrink-0 mr-2">
                                {{ '生日獎賞進度' }}
                                <div class="icon align-bottom tooltip">
                                    <infoSvg />
                                    <span class="tooltiptext">
                                        {{
                                            $t(
                                                'account.birthday.tooltip.message',
                                                brthAwardMinPymtRqr,
                                                new Date().getFullYear(),
                                                brthAwardTcoinNum
                                            )
                                        }}
                                    </span>
                                </div>
                            </div>
                            <div class="progress-bar">
                                <div
                                    style="position: absolute; left: 50%; transform: translate(-50%, 0);"
                                >
                                    {{
                                        `${totalPymtAmtLastYear}/${brthAwardMinPymtRqr}`
                                    }}
                                </div>
                                <div
                                    class="h-6 bg-primary rounded-l"
                                    :style="{
                                        width: progressBarPercentage + '%'
                                    }"
                                ></div>
                            </div>
                        </div>
                        <div
                            v-if="
                                allowEditBirthday &&
                                    showBirthdayTcoinGiftMessage
                            "
                            class="text-xs text-blue"
                        >
                            {{ $t('account.birthday.gift.message') }}
                        </div>
                    </div>
                </template>
            </formFieldRowByRule>
            <div class="w-full"></div>
            <formFieldRowByRule
                name="phone"
                :label="$t('account.phone')"
                :errors="errors"
                :rules="validationRules"
                class="field-phone"
            >
                <template #default="{ hasError }">
                    <input
                        class="form-input w-full"
                        :class="{ 'border-red': hasError }"
                        type="tel"
                        v-model.trim="form.phone"
                        :readonly="phoneNumberVerified"
                    />
                    <div v-if="phoneNumberVerified" class="text-xs">
                        {{ $t('account.phone.verified') }}
                    </div>
                </template>
            </formFieldRowByRule>
            <div class="w-full"></div>
            <div class="mb-4 sm:ml-32 sm:pl-4 text-sm">
                <div class="require-message mb-2">
                    {{ $t('common.required') }}
                </div>
                <agreeTermsCheckbox
                    class="checkbox-sm"
                    v-model="form.agreeTerms"
                />
                <checkbox
                    v-model="form.agreePromotions"
                    v-if="showAgreePromotions"
                    class="checkbox-sm"
                >
                    {{ $t('account.agree.promotion') }}
                </checkbox>
            </div>
        </component>
        <errorBox :message="errors.server" />
        <slot name="buttons" :saveStatus="saveStatus" :save="save">
            <div class="flex justify-end">
                <loadingButton
                    class="bg-primary text-white border-0 text-lg px-12 sm:mr-2"
                    :status="saveStatus"
                    @click="save"
                    @successShown="saveStatus = undefined"
                >
                    {{ $t('common.save') }}
                </loadingButton>
            </div>
        </slot>
    </div>
</template>

<script>
import checkbox from '@/components/checkbox.vue';
import formFieldRow from '@/components/formFieldRow.vue';
import formFieldRowByRule from '@/components/formFieldRowByRule.vue';
import loadingButton from '@/components/loadingButton.vue';
import { instance } from '@/utils/ajax';
import { validate } from '@/utils/validationUtil';
import errorBox from '@/components/errorBox.vue';
import monthPicker from '@/components/monthPicker.vue';
import { wait } from '@/utils/promiseUtil';
import formatDate from '../components/formatDateFilter';
import agreeTermsCheckbox from '@/components/agreeTermsCheckbox.vue';
import infoSvg from '@/assets/svg/info_outline.svg';

async function getInfo() {
    try {
        let result = await instance.get('/api/accountInfo/getReferenceData');
        return result.data;
    } catch (e) {
        /*eslint-disable no-console*/
        console.error('error getting info');
    }
    return undefined;
}

async function save(form) {
    try {
        let result = await instance.post('/api/accountInfo/save', form);
        return result.data;
    } catch (e) {
        /*eslint-disable no-console*/
        console.error('error register');
        return { success: false, all: 'error.connect' };
    }
}

const validationRulesGeneral = {
    title: { required: true, show: true },
    surname: { required: true, show: true },
    givenName: { required: true, show: true },
    email: { required: true, show: true },
    deliverySite: { required: true, show: true },
    departmentName: { required: true, show: true },
    jobLevel: { required: true, show: true },
    monthlyIncome: { required: true, show: true },
    birthday: { required: true, show: true },
    phone: { required: true, show: true },
    occupation: { required: !!false, show: !!false },
    agreeTerms: {
        checked: function(val) {
            if (val !== true) {
                return 'register.error.agree.terms';
            }
        }
    }
};

const validationRulesFriends = {
    ...validationRulesGeneral,
    departmentName: { required: !!false, show: !!false },
    occupation: { required: !!false, show: true },
    deliverySite: { required: !!false, show: !!false },
    jobLevel: { required: !!false, show: true },
    monthlyIncome: { required: !!false, show: true },
    birthday: { required: !!false, show: true }
};

const validationRulesPublic = {
    ...validationRulesGeneral,
    departmentName: { required: !!false, show: !!false },
    occupation: { required: !!false, show: true },
    deliverySite: { required: !!false, show: !!false },
    jobLevel: { required: !!false, show: true },
    monthlyIncome: { required: !!false, show: true },
    birthday: { required: !!false, show: true }
};

export default {
    components: {
        checkbox,
        formFieldRow,
        formFieldRowByRule,
        loadingButton,
        errorBox,
        monthPicker,
        agreeTermsCheckbox,
        infoSvg
    },
    props: {
        showAgreePromotions: { default: true },
        formWrapper: { default: 'div' },
        showBirthdayAwardMessage: { default: true }
    },
    data() {
        const u = this.$store.state.user;
        return {
            genderCodeList: [],
            siteList: [],
            jobLevelList: [],
            monthlyIncomeList: [],
            occupationList: [],
            customerId: u.custId,
            customerTypeCode: u.custTypeCode,
            form: {
                title: u.custGndrCode,
                surname: u.custLastName,
                givenName: u.custFrstName,
                birthday: u.custBrthMthDate,
                jobLevel: u.custJobLvlSlctKey,
                monthlyIncome: u.custMthIncmSlctKey,
                email: u.custEmailAddr,
                phone: u.custPhoneNum,
                agreePromotions: u.sbscPrmtEmailInd === 'Y',
                deliverySite: u.cmpySiteKey,
                departmentName: u.deptName,
                occupation: u.custOcpySlctKey,
                agreeTerms: true
            },
            errors: {},
            saveStatus: undefined,
            allowEditEmail: u.emailAddrVrfyInd !== 'Y',
            allowEditBirthday: !u.custBrthMthDate,
            phoneNumberVerified: u.phoneNumVrfyInd === 'Y',
            showBirthdayTcoinGiftMessage: false,
            showTerms: false,
            showPrivacy: false,
            brthAwardTcoinNum: undefined,
            brthAwardMinPymtRqr: undefined,
            lastBrthAwardIssYear: undefined,
            totalPymtAmtLastYear: undefined
        };
    },
    beforeCreate() {
        wait(() => this.$store.dispatch('getAccountInfo'), 500);
    },
    async created() {
        const result = await getInfo();
        if (!result.data) {
            return;
        }
        this.jobLevelList = result.data.jobLevelList.map(i => ({
            slctDesp: i.slctDesp,
            id: i.id
        }));
        this.monthlyIncomeList = result.data.monthlyIncomeList.map(i => ({
            slctDesp: i.slctDesp,
            id: i.id
        }));
        this.siteList = this.$store.state.companySiteList;
        this.genderCodeList = result.data.genderCodeList;
        this.occupationList = result.data.occupationList;
        this.showBirthdayTcoinGiftMessage =
            result.data.showBirthdayTcoinGiftMessage;
        this.brthAwardTcoinNum = result.data.brthAwardTcoinNum;
        this.brthAwardMinPymtRqr = result.data.brthAwardMinPymtRqr;
        this.lastBrthAwardIssYear = result.data.lastBrthAwardIssYear;
        this.totalPymtAmtLastYear = result.data.totalPymtAmtLastYear;
    },
    computed: {
        email: {
            get: function() {
                return this.form.email;
            },
            set: function(v) {
                if (this.allowEditEmail) {
                    this.form.email = v;
                }
            }
        },
        validationRules() {
            const custTypeCode = this.$store.state.user?.custTypeCode;
            return custTypeCode === 'PUB'
                ? validationRulesPublic
                : custTypeCode === 'FRND'
                ? validationRulesFriends
                : validationRulesGeneral;
        },
        birthAwardDistributed() {
            const currentYear = new Date().getFullYear();
            return this.lastBrthAwardIssYear >= currentYear;
        },
        achievedMinPymtRqr() {
            return this.totalPymtAmtLastYear >= this.brthAwardMinPymtRqr;
        },
        progressBarPercentage() {
            return (this.totalPymtAmtLastYear / this.brthAwardMinPymtRqr) * 100;
        },
        canGetBirthdayAward() {
            const customer = this.$store.state.user;
            return customer.custTypeCode === 'ENT';
        }
    },
    methods: {
        formatDate: formatDate,
        async save() {
            this.errors = {};
            this.form.phone = this.form.phone?.replace(/\s/g, '');
            const v = validate(this.errors, this.form, this.validationRules);
            if (v.hasError) {
                this.errors = v.errors;
                this.scrollToError();
                return;
            }
            this.saveStatus = 'loading';
            const result = await save(this.form);
            if (result.success) {
                this.saveStatus = 'success';
                const user = result.data.user;
                this.$store.commit('setUser', user);
                this.allowEditEmail = user.emailAddrVrfyInd !== 'Y';
                this.allowEditBirthday = !user.custBrthMthDate;
                this.$emit('successSaved');
            } else {
                this.saveStatus = undefined;
                this.errors = result.data.errors;
            }
        },
        scrollToError() {
            const field = Object.keys(this.errors)[0];
            if (field) {
                const target = this.$el.querySelector('.field-' + field);
                target.scrollIntoView();
            }
        }
    }
};
</script>
